/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-10-06 11:39:10
 *
 *******************************************************/
// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import { Card, SkeletonDisplayText, TextStyle, Badge } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import RecommendedProductItem from 'src/components/views/recommendation/manual-recommendation/item/recommended-product-item';
import NoProduct from 'src/components/views/recommendation/manual-recommendation/item/no-product';
import { reorder } from 'src/helpers';
import type { Product } from 'src/models/product';

type Props = {
  sourceProducts: Product[],
  recommendedProducts: Product[],
  isLoading: boolean,
  onToggleModal: (modal: string) => void,
  onRemove: (product: Product) => void,
  onDragEnd: (recommendedProducts: Product[]) => void
};

type ComposedProps = WithI18nProps & Props;

class RecommendProduct extends Component<ComposedProps> {
  render() {
    const { i18n, sourceProducts, onToggleModal } = this.props;
    return (
      <div className="BC-Page__ManualRecommendationNew__RecommendProduct">
        <DragDropContext onDragEnd={this.handleDragEnd}>
          <Card
            sectioned
            title={
              <div>
                <div>
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.Recommendation.ManualRecommendation.AddNew.recommendProduct_Title'
                    )}
                  </TextStyle>
                  <span style={{ marginLeft: '0.25rem' }}>
                    <Badge status="info">
                      {i18n.translate(
                        'AdminHTML.Recommendation.ManualRecommendation.AddNew.recommendProduct_Badge'
                      )}
                    </Badge>
                  </span>
                </div>
                <div>
                  <TextStyle variation="subdued">
                    {i18n.translate(
                      'AdminHTML.Recommendation.ManualRecommendation.AddNew.recommendProduct_Subtitle'
                    )}
                  </TextStyle>
                </div>
              </div>
            }
            actions={
              _.isEmpty(sourceProducts)
                ? null
                : [
                    {
                      content: i18n.translate(
                        'AdminHTML.Recommendation.ManualRecommendation.AddNew.addProduct'
                      ),
                      onAction: onToggleModal
                    }
                  ]
            }>
            {this.renderContent()}
          </Card>
        </DragDropContext>
      </div>
    );
  }

  renderContent = () => {
    const { isLoading, recommendedProducts, onRemove } = this.props;
    if (isLoading) {
      return (
        <div className="BC-Page__ManualRecommendationNew__RecommendProduct__Skeleton">
          <SkeletonDisplayText size="medium" />
        </div>
      );
    }

    if (_.isEmpty(recommendedProducts)) {
      return <NoProduct />;
    }

    return (
      <Droppable droppableId="droppable">
        {(provided, snapshot) => (
          <ul
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="BC-Page__ManualRecommendationNew__RecommendProduct__List BC-Draggable__Container">
            {recommendedProducts.map((product, index) => (
              <Draggable key={product.id} draggableId={product.id} index={index}>
                {(provided, snapshot) => {
                  return (
                    <li ref={provided.innerRef} {...provided.draggableProps}>
                      <RecommendedProductItem
                        product={product}
                        draggableProps={provided.draggableProps}
                        dragHandleProps={provided.dragHandleProps}
                        onRemove={onRemove}
                      />
                    </li>
                  );
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    );
  };

  handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // reordered index of products and update
    const { recommendedProducts, onDragEnd } = this.props;
    const reorderedRecommendedProducts = reorder(
      recommendedProducts,
      result.source.index,
      result.destination.index
    );
    onDragEnd(reorderedRecommendedProducts);
  };
}

export default withI18n()(RecommendProduct);
